import React, { useState, useEffect } from "react";
import Input from "../../../../core/Inputs/DefaultInput";
import Button from "../../../../core/Button";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Svg from "../../../../component/common/Svg";
import Axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as Yup from "yup";

const passwordExpiredSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required("New password is required")
    .min(12, "Password must be at least 12 characters")
    .matches(/[A-Z]/, "Password must contain an uppercase letter")
    .matches(/[a-z]/, "Password must contain a lowercase letter")
    .matches(/[0-9]/, "Password must contain a number")
    .matches(/[\W_]/, "Password must contain a special character"),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("newPassword"), null], "Passwords do not match"),
});

const PasswordExpired = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get("email");

  const [passwordType, setPasswordType] = useState("password");
  const [confirmPasswordType, setConfirmPasswordType] = useState("password");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!email) {
      navigate("/admin/login");
    }
  }, [email, navigate]);

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: passwordExpiredSchema,
    onSubmit: (values) => {
      setSubmitting(true);
      Axios.post(process.env.REACT_APP_NODE_API_URL + "/users/changePassword", {
        user_email: email,
        new_password: values.newPassword,
      })
        .then((response) => {
          if (response.data.statusCode === 200) {
            toast.success("Password changed successfully! Please log in.");
            navigate("/admin/login");
          } else {
            toast.error(response.data.msg || "Failed to change password.");
          }
        })
        .catch((error) => {
          const msg =
            error?.response?.data?.msg || "Something went wrong. Try again.";
          toast.error(msg);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const { values, handleChange, handleBlur, touched, errors, handleSubmit } = formik;

  return (
    <div className="d-flex flex-column site-main-wrapper login-page">
      <div className="flex-grow-1 main-content-wrap d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="form-outer-wrapper">
            <div className="logo-wrap text-center">
              <Link to="/#0t" className="home-logo">
                <Svg name="logo" />
              </Link>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-wrapper">
                <div className="form-heading text-center">
                  <h3>Password Expired</h3>
                  <p>
                    Your password has expired. Please set a new password to
                    continue. Your new password must be different from your
                    current password.
                  </p>
                </div>
                <div className="field-wrapper">
                  <div className={`mb__20 position-relative ${touched.newPassword ? (errors.newPassword ? 'error' : 'success') : ''}`}>
                    <Input
                      type={passwordType}
                      view="outlined"
                      color="default"
                      label=""
                      placeholder="Enter new password"
                      fill
                      className="formsinput"
                      name="newPassword"
                      value={values.newPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.newPassword && errors.newPassword && (
                      <div className="validation-msg">{errors.newPassword}</div>
                    )}
                    <div className="password-requirement">
                      <p>Requirements</p>
                      <ul>
                        <li className={values.newPassword.length >= 12 ? "valid" : "invalid"}>
                          Minimum of 12 characters
                        </li>
                        <li className={/[A-Z]/.test(values.newPassword) ? "valid" : "invalid"}>
                          Contains an uppercase letter
                        </li>
                        <li className={/[a-z]/.test(values.newPassword) ? "valid" : "invalid"}>
                          Contains a lowercase letter
                        </li>
                        <li className={/[0-9]/.test(values.newPassword) ? "valid" : "invalid"}>
                          Contains a number (0-9)
                        </li>
                        <li className={/[\W_]/.test(values.newPassword) ? "valid" : "invalid"}>
                          Contains a special character
                        </li>
                      </ul>
                    </div>
                    <span
                      className={"forgot-wrap " + passwordType}
                      onClick={() =>
                        setPasswordType(
                          passwordType === "password" ? "text" : "password"
                        )
                      }
                    >
                      <i className="fa fa-eye" aria-hidden="true"></i>
                    </span>
                  </div>
                  <div className={`mb__20 position-relative ${touched.confirmPassword ? (errors.confirmPassword ? 'error' : 'success') : ''}`}>
                    <Input
                      type={confirmPasswordType}
                      view="outlined"
                      color="default"
                      label=""
                      placeholder="Confirm new password"
                      fill
                      className="formsinput"
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.confirmPassword && errors.confirmPassword && (
                      <div className="validation-msg">{errors.confirmPassword}</div>
                    )}
                    <span
                      className={"forgot-wrap " + confirmPasswordType}
                      onClick={() =>
                        setConfirmPasswordType(
                          confirmPasswordType === "password" ? "text" : "password"
                        )
                      }
                    >
                      <i className="fa fa-eye" aria-hidden="true"></i>
                    </span>
                  </div>
                </div>
                <div className="button-wrap">
                  <button
                    type="submit"
                    className="formsbutton btn-primary btn mb__20 w-100"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="white-spinner spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span className="visually-hidden">Loading...</span>
                      </>
                    ) : (
                      "Set new password"
                    )}
                  </button>
                </div>
                <div className="form-text text-center mt-3">
                  <p>
                    <Link to="/admin/login">Back to Login</Link>
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordExpired;
